<template>
  <div class="threshold-coupon-wrapper" v-if="visible">
    <div class="threshold-coupon-row">
      <lazy-image :img-link="promoIcon" image-class="coupon-promo-icon" wrapper-class="coupon-promo-icon-wrapper" />
      <p
        class="coupon-promo-desc"
        v-html="thresholdCouponDesc">
      </p>
    </div>
    <div class="threshold-coupon-row threshold-coupon-row-progress">
      <span class="discount-min-amount">{{ $money(0, { fixedLength: 0 }) }}</span>
      <zm-progress
        :percentage="viewPercentage"
        :show-text="false"
        :stroke-width="9"
        color="#66CC84"
        class="threshold-coupon-progress"
      />
      <span class="discount-max-amount">{{ $money(maxDiscountAmount * 100, { fixedLength: 0}) }}</span>
    </div>
  </div>
</template>

<script>
import LazyImage from '@/components/lazy-image.vue';
import promoIcon from '@/assets/coupon/gift.svg';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { couponSourceTypeMap } from '@/views/config/coupon/helper/constant';
import { Progress as ZmProgress } from '@zoom/zoom-ui';
import { getOnlinePlanName } from '@/common/product';
import messageMixin from '@/common/mixin/message';

export default {
  name: 'ThresholdCouponProcessBar',
  components: { LazyImage, ZmProgress },
  mixins: [messageMixin],
  data() {
    return {
      promoIcon,
      viewPercentage: 0,
    };
  },
  computed: {
    ...mapGetters([
      'discountWithAmountMap',
      'couponState',
      'groupByPlanCodePlanMap'
    ]),
    isThresholdCoupon() {
      return this.couponState?.couponSourceType === couponSourceTypeMap.threshold;
    },
    visible() {
      return this.isThresholdCoupon && this.nextTierAmount > 0;
    },
    actualTierAmount() {
      return _.get(this.discountWithAmountMap, 'thresholdResult.actualTierAmount', 0);
    },
    actualDiscountAmount() {
      return _.get(this.discountWithAmountMap, 'thresholdResult.actualDiscountAmount', 0)
    },
    nextTierAmount() {
      return _.get(this.discountWithAmountMap, 'thresholdResult.nextTierAmount', 0);
    },
    nextDiscountAmount() {
      return _.get(this.discountWithAmountMap, 'thresholdResult.nextDiscountAmount', 0);
    },
    maxDiscountAmount() {
      return this.actualDiscountAmount ? this.actualTierAmount : this.nextTierAmount;
    },
    gapTotalAmount() {
      return _.get(this.discountWithAmountMap, 'thresholdResult.gapTotalAmount', 0);
    },
    percentage() {
      const totalAmount = _.get(this.discountWithAmountMap, 'thresholdResult.totalAmount', 0);

      if (this.nextTierAmount) {
        const curPercentage = (totalAmount / this.maxDiscountAmount) * 100;
        return curPercentage > 100 ? 100 : curPercentage;
      }

      return 100;
    },
    thresholdCouponDesc() {
      const addMoreParams = [this.$money(this.gapTotalAmount * 100), this.$money(this.nextDiscountAmount * 100, { fixedLength: 0 })];

      return this.$t('billing.coupon.threshold.need_add_more_desc', addMoreParams);
    },
  },
  methods: {
    // fixed render traffic, impact transition
    animatePercentage(viewPercentage) {
      this.viewPercentage = viewPercentage;
    },
    getDiscountProductName() {
      const discountPlanInfo = this.discountWithAmountMap?.discountPlans || {};

      return _.chain(discountPlanInfo)
      .keys()
      .map((planCode) => {
        const plan = _.get(this.groupByPlanCodePlanMap, [planCode]);
        return plan ? getOnlinePlanName(plan) : undefined;
      })
      .filter(Boolean)
      .uniq()
      .join(", ")
      .value();
    },
  },
  watch: {
    percentage: {
      immediate: true,
      handler(val) {
        this.animatePercentage(val);
      },
    },
    actualDiscountAmount(val, oldVal) {
      if (this.isThresholdCoupon && !val) {
        this.hideMessages();
      }

      if (this.isThresholdCoupon && val > 0 && val > oldVal) {
        this.showMessage({
          message: this.$t('billing.coupon.threshold.success_get_discount_desc',[this.$money(this.actualDiscountAmount * 100, {fixedLength: 0})]),
          type: 'success',
          duration: -1,
          showClose: true,
          onClose: () => {
            this.$tracking.buyInteraction(
              `banner - product level`,
              `${this.getDiscountProductName()} - congratulations! you’ve received a discount - close`
            );
          }
        });
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.getDiscountProductName()} - congratulations! you’ve received a discount - impression`
        );
      }
    },
  },
};
</script>

<style lang="scss">
.threshold-coupon-wrapper {
  margin-bottom: 24px;

  .threshold-coupon-row {
    display: flex;
    align-items: center;

    .coupon-promo-icon-wrapper {
      flex: 0 0 20px;
    }

    .coupon-promo-icon {
      width: 20px;
      height: 24px;
    }

    .coupon-promo-desc {
      margin-left: 7px;
      color: #2A2B2D;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px; /* 128.571% */
      letter-spacing: -0.15px;
    }

    .need-add-more-amount {
      font-weight: 600;
    }
  }

  .threshold-coupon-row-progress {
    font-family: 'Lato','Inter';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: rgba(4, 4, 19, 0.56);
    margin-top: 12px;

    .zm-progress-bar__outer {
      background: #ffffff;
    }

    .threshold-coupon-progress {
      width: 100%;

      .zm-progress-bar__inner {
        transition: width .5s ease-in-out;
      }
    }

    .discount-min-amount, .discount-max-amount {
      color: #2A2B2D;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px; /* 128.571% */
      letter-spacing: -0.15px;
      white-space: nowrap;
    }

    .discount-min-amount {
      margin-right: 8px;
    }

    .discount-max-amount {
      margin-left: 8px;
    }
  }
}
</style>
